/**
 * Guided "/" shortcuts menu state machine for ChatInput.
 *
 * Ported from the floating Ask Heidi bar so the full-window composer can keep
 * TipTap focused while the Radix menu is open (modal={false} + no autofocus).
 * Owns the guided-shortcuts flag read so Chat callers never import
 * `@/features/flags` — see src/components/chat/CLAUDE.md.
 *
 * The floating bar still inlines its own copy; this hook is the ChatInput
 * adaptation, not a shared replacement yet.
 */
import { useIsAskHeidiSourcesShortcutsEnabled } from '@/app-shell/flags/evidence-flags';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isEditorLive } from '@heidi/editor/core';
import type { Editor } from '@tiptap/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import type { ProtocolsButtonHandle } from './protocols-button.types';

interface UseSlashShortcutMenuArgs {
  editor: Editor | null | undefined;
  /** True only when the shortcuts menu is actually rendered and interactive. */
  enabled: boolean;
  pinnedChipsCount: number;
  isPreNoteSession?: boolean;
}

interface UseSlashShortcutMenu {
  isGuidedEnabled: boolean;
  isOpen: boolean;
  handleOpenChange: (open: boolean) => void;
  /** Raw close with no editor side effects (chip-click send path). */
  closeMenu: () => void;
  shortcutQuery: string;
  protocolsRef: RefObject<ProtocolsButtonHandle>;
  retainEditorFocus: () => void;
  /** Wire to a container onKeyDown; returns true when it consumed the event. */
  handleSlashKeyDown: (e: KeyboardEvent) => boolean;
  /** True while "/" command mode owns Enter — use to disable send hotkeys. */
  isSlashCommandActive: boolean;
}

interface SlashQueryState {
  active: boolean;
  query: string;
}

const INACTIVE_SLASH: SlashQueryState = { active: false, query: '' };

export function useSlashShortcutMenu({
  editor,
  enabled,
  pinnedChipsCount,
  isPreNoteSession = false,
}: UseSlashShortcutMenuArgs): UseSlashShortcutMenu {
  const isGuidedEnabled = useIsAskHeidiSourcesShortcutsEnabled();
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const isGuidedEnabledRef = useRef(isGuidedEnabled);
  isGuidedEnabledRef.current = isGuidedEnabled;
  const pinnedChipsCountRef = useRef(pinnedChipsCount);
  pinnedChipsCountRef.current = pinnedChipsCount;
  const isPreNoteSessionRef = useRef(isPreNoteSession);
  isPreNoteSessionRef.current = isPreNoteSession;

  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Only update React state when slash-mode or the query actually changes —
  // do not mirror the full composer document on every keystroke.
  const [slashState, setSlashState] = useState<SlashQueryState>(INACTIVE_SLASH);
  const startsWithSlashRef = useRef(false);
  const isShortcutSearchingRef = useRef(false);

  const applySlashState = (rawText: string) => {
    const active = rawText.startsWith('/');
    const query = active ? rawText.slice(1) : '';
    startsWithSlashRef.current = active;
    isShortcutSearchingRef.current = query.trim().length > 0;
    setSlashState((prev) =>
      prev.active === active && prev.query === query ? prev : { active, query }
    );
  };

  const protocolsRef = useRef<ProtocolsButtonHandle>(null);

  const handleOpenChangeRef = useRef<(open: boolean) => void>(() => {});

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!isEditorLive(editor, 'use-slash-shortcut-menu.handleOpenChange')) return;
      if (open) {
        // Only seed "/" when opening from an empty editor (e.g. the trigger
        // button). If the user typed "/prep" to open the menu, leave it be
        // so the search query is preserved.
        if (editor.getText().trim() === '') {
          editor.chain().clearContent().insertContent('/').run();
        }
        // Guided only: keep caret in TipTap so "/prep" typing works (menu is
        // modal={false} + open autofocus prevented under the same flag).
        if (isGuidedEnabledRef.current) {
          editor.commands.focus('end');
        }
      } else {
        // Closing (selection / escape / outside click): drop in-progress
        // slash text. Guided clears any "/…" search; legacy only clears a
        // bare "/" so typed "/prep" (sendable in that cohort) is preserved.
        const text = editor.getText();
        const shouldClear = isGuidedEnabledRef.current
          ? text.startsWith('/')
          : text.trim() === '/';
        if (shouldClear) {
          editor.commands.clearContent();
        }
        editor.commands.focus('end');
      }
    },
    [editor]
  );
  handleOpenChangeRef.current = handleOpenChange;

  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      if (!enabledRef.current) return;
      const rawText = editor.getText();
      applySlashState(rawText);
      // Draft restoration sets content before its own focus() call — ignore
      // that update so a saved "/…" draft never auto-opens the menu on mount.
      if (!editor.isFocused) return;

      const openViaSlash = () => {
        // Controlled `open` skips Radix onOpenChange, so slash opens never
        // hit ProtocolsButton's trigger tracking — emit here instead.
        track({
          event_name: TrackingEventName.EvidenceShortcutsOpened,
          props: {
            shortcut_count: pinnedChipsCountRef.current,
            is_pre_note_session: isPreNoteSessionRef.current,
            open_method: 'slash',
          },
          options: { amplitude: false },
        });
        handleOpenChangeRef.current(true);
      };

      if (isGuidedEnabledRef.current) {
        const opensWithSlash = rawText.startsWith('/');
        if (opensWithSlash && !isOpenRef.current) {
          openViaSlash();
        } else if (!opensWithSlash && isOpenRef.current) {
          handleOpenChangeRef.current(false);
        }
      } else if (rawText === '/' && !isOpenRef.current) {
        openViaSlash();
      }
    };

    handleUpdate();
    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, enabled]);

  // ProtocolsButton unmounts its dropdown outright when `disabled` goes true
  // and never calls onOpenChange(false). Force-close so leftover "/query"
  // cannot be sent as a chat message once isSlashCommandActive drops.
  useEffect(() => {
    if (!enabled && isOpenRef.current) {
      handleOpenChangeRef.current(false);
    }
  }, [enabled]);

  const retainEditorFocus = useCallback(() => {
    if (
      isEditorLive(editor, 'use-slash-shortcut-menu.retainEditorFocus') &&
      !editor.isFocused
    ) {
      editor.commands.focus('end');
    }
  }, [editor]);

  const handleSlashKeyDown = useCallback((e: KeyboardEvent): boolean => {
    if (
      !enabledRef.current ||
      !isGuidedEnabledRef.current ||
      !startsWithSlashRef.current ||
      !isOpenRef.current
    ) {
      return false;
    }
    const protocols = protocolsRef.current;
    if (isShortcutSearchingRef.current && protocols?.hasResults()) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        protocols.moveHighlight(1);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        protocols.moveHighlight(-1);
        return true;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (protocols.runHighlighted()) {
          handleOpenChangeRef.current(false);
        }
        return true;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  }, []);

  const isSlashCommandActive = enabled && isGuidedEnabled && slashState.active && isOpen;

  return {
    isGuidedEnabled,
    isOpen,
    handleOpenChange,
    closeMenu,
    shortcutQuery: slashState.query,
    protocolsRef,
    retainEditorFocus,
    handleSlashKeyDown,
    isSlashCommandActive,
  };
}
